import { useMemo } from 'react';
import { useAppSelector } from '../app/hooks';
import { selectTopLevelMenuItems } from '../selectors/menu';
import { useReminder } from '../contexts/ReminderContext';
import { TTS_PROMPT, TTS_PROMPT_PREFIX } from '../constants';
import { SLASH_COMMAND_REMINDERS } from '../utils/constants';
import { SlashCommand } from '../types/menu';

interface SlashCommandReminderResult {
  hasRequiredCommands: boolean;
  availableSlashCommands: SlashCommand[];
  showSlashCommandReminder: () => void;
}

/**
 * Custom hook that provides slash command reminder functionality.
 * Detects available slash commands from TTS prompt items and provides
 * a function to show the appropriate reminder message.
 */
export const useSlashCommandReminder = (): SlashCommandReminderResult => {
  const topLevelMenuItems = useAppSelector(selectTopLevelMenuItems) || {};
  const { showReminder } = useReminder();

  // Get the actual slash commands from tts-prompt category items
  const { hasRequiredCommands, availableSlashCommands } = useMemo(() => {
    const ttsPromptItems = Object.values(topLevelMenuItems).filter(
      (item) =>
        item.category.includes(TTS_PROMPT) ||
        item.category.includes(TTS_PROMPT_PREFIX)
    );

    // Filter items that have the required commands and they're active
    const relevantItems = ttsPromptItems.filter((item) => {
      if (!item.inactive && item.name) {
        const [intent] = item.name.split(':');
        return [
          SLASH_COMMAND_REMINDERS.DONATION,
          SLASH_COMMAND_REMINDERS.CUSTOMER_INFO,
        ].includes(intent);
      }
      return false;
    });

    // Sort items to ensure donation comes before customer_info
    const sortedItems = relevantItems.sort((a, b) => {
      const aIsDonation = a.name?.includes(SLASH_COMMAND_REMINDERS.DONATION);
      const bIsDonation = b.name?.includes(SLASH_COMMAND_REMINDERS.DONATION);
      const aIsCustomerInfo = a.name?.includes(
        SLASH_COMMAND_REMINDERS.CUSTOMER_INFO
      );
      const bIsCustomerInfo = b.name?.includes(
        SLASH_COMMAND_REMINDERS.CUSTOMER_INFO
      );

      // Donation items come first
      if (aIsDonation && !bIsDonation) return -1;
      if (!aIsDonation && bIsDonation) return 1;

      // Customer info items come after donation
      if (aIsCustomerInfo && !bIsCustomerInfo && !bIsDonation) return -1;
      if (!aIsCustomerInfo && bIsCustomerInfo && !aIsDonation) return 1;

      // Other items maintain their relative order
      return 0;
    });

    // Extract commands from sorted items
    const commands: SlashCommand[] = [];
    sortedItems.forEach((item) => {
      if (item.slashCommands && item.slashCommands.length > 0) {
        commands.push({ intent: item.name, command: item.slashCommands[0] });
      } else {
        // Handle PRP menu setup
        const [intent, command] = item.name.split(':');
        if (command) {
          commands.push({ intent, command });
        }
      }
    });

    return {
      hasRequiredCommands: relevantItems.length > 0,
      availableSlashCommands: commands,
    };
  }, [topLevelMenuItems]);

  /**
   * Shows the appropriate slash command reminder message.
   * Only shows reminder if required commands exist in tts-prompt category.
   */
  const showSlashCommandReminder = () => {
    if (hasRequiredCommands) {
      let message = 'Remember to ask for ';
      if (availableSlashCommands.length === 1) {
        message +=
          availableSlashCommands[0].intent === SLASH_COMMAND_REMINDERS.DONATION
            ? 'donation'
            : availableSlashCommands[0].intent ===
              SLASH_COMMAND_REMINDERS.CUSTOMER_INFO
            ? 'customer name'
            : '';
      } else {
        message += 'donation and customer name';
      }
      showReminder(message, availableSlashCommands);
    }
  };

  return {
    hasRequiredCommands,
    availableSlashCommands,
    showSlashCommandReminder,
  };
};

export default useSlashCommandReminder;
